import html2pdf from "html2pdf.js";
import React, { useState, useEffect, useCallback } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import "./theme.css";
import { format, addDays } from "date-fns";
import { debounce } from "lodash";
import Confetti from "react-confetti";

const LEVERAGE_OPTIONS = [
  { value: 20, label: "1:20" },
  { value: 30, label: "1:30" },
  { value: 50, label: "1:50" },
  { value: 100, label: "1:100" },
  { value: 200, label: "1:200" },
  { value: 300, label: "1:300" },
  { value: 400, label: "1:400" },
  { value: 500, label: "1:500" },
  { value: 1000, label: "1:1000" },
];

const DEFAULT_COMMISSION_PER_LOT = 6; // Default commission of $10 per lot

const CACHE_DURATION = 1 * 60 * 1000; // 1 minute in milliseconds

function App() {
  const [formData, setFormData] = useState(() => {
    const savedFormData = localStorage.getItem("tradingPlanFormData");
    return savedFormData
      ? JSON.parse(savedFormData)
      : {
          balance: "",
          leverage: "",
          tradingDays: "",
          commission: "",
        };
  });

  const [goldPrice, setGoldPrice] = useState(null);
  const [goldPriceDisplay, setGoldPriceDisplay] = useState(null);
  const [tradingPlan, setTradingPlan] = useState(() => {
    const savedPlan = localStorage.getItem("tradingPlanCache");
    return savedPlan ? JSON.parse(savedPlan) : [];
  });
  const [showResults, setShowResults] = useState(() => {
    return localStorage.getItem("tradingPlanCache") !== null;
  });
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem("theme") || "light";
  });
  const [isLoading, setIsLoading] = useState(false);
  const [cacheKey, setCacheKey] = useState(() => {
    return localStorage.getItem("tradingPlanCacheKey") || "";
  });
  const [balanceError, setBalanceError] = useState("");

  // Updated Constants for XAU/USD calculations
  const DAILY_PIPS = 200;
  const PIP_VALUE_PER_LOT = 1; // 1 lot = $1 per pip for XAU/USD
  const MAX_ALLOWED_LOT_SIZE = 50; // Increased from 5 to 50 lots
  const OUNCES_PER_LOT = 100; // 1 standard lot = 100 ounces of gold

  // Helper function to format numbers with commas
  const formatNumber = (number) => {
    const formatted = Number(number).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return formatted;
  };

  const checkBalanceDebounced = useCallback((value) => {
    const debouncedCheck = debounce((val) => {
      if (val && parseFloat(val) < 100) {
        setBalanceError("Bala Manyake");
      } else {
        setBalanceError("");
      }
    }, 300);
    debouncedCheck(value);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let updatedFormData = { ...formData };

    // Special handling for balance field
    if (name === "balance") {
      const numValue = parseFloat(value);
      if (value === "" || (numValue > 0 && numValue <= 100000)) {
        updatedFormData[name] = value;
        checkBalanceDebounced(value);
      }
    }
    // Special handling for tradingDays field
    else if (name === "tradingDays") {
      const numValue = parseInt(value);
      if (value === "" || (numValue > 0 && numValue <= 365)) {
        updatedFormData[name] = value;
      }
    }
    // Handle other fields
    else {
      const numValue = parseFloat(value);
      if (value === "" || numValue > 0) {
        updatedFormData[name] = value;
      }
    }

    setFormData(updatedFormData);
    localStorage.setItem(
      "tradingPlanFormData",
      JSON.stringify(updatedFormData)
    );

    // Clear cached results when inputs change
    if (cacheKey) {
      resetCalculator();
    }
  };

  useEffect(() => {
    const cachedData = localStorage.getItem("goldPriceCache");
    if (cachedData) {
      const { price, timestamp } = JSON.parse(cachedData);
      const now = Date.now();

      if (now - timestamp < CACHE_DURATION) {
        setGoldPrice(price);
        setGoldPriceDisplay(price.toFixed(2));
        return;
      }
    }
    fetchGoldPrice();
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const fetchGoldPrice = async () => {
    const now = Date.now();
    const cachedData = localStorage.getItem("goldPriceCache");

    if (cachedData) {
      const { price, timestamp } = JSON.parse(cachedData);

      // Check if cached data is still valid (within 5 minutes)
      if (now - timestamp < CACHE_DURATION) {
        setGoldPrice(price);
        setGoldPriceDisplay(price.toFixed(2));
        return;
      }
    }

    setIsLoading(true);
    try {
      const response = await fetch("https://api.gold-api.com/price/XAU");
      const data = await response.json();

      if (data.price) {
        const goldPriceUSD = data.price;

        // Save to localStorage with timestamp
        localStorage.setItem(
          "goldPriceCache",
          JSON.stringify({
            price: goldPriceUSD,
            timestamp: now,
          })
        );

        setGoldPrice(goldPriceUSD);
        setGoldPriceDisplay(goldPriceUSD.toFixed(2));
      } else {
        throw new Error("Failed to fetch gold price");
      }
    } catch (error) {
      console.error("Error fetching gold price:", error);
      const fallbackPrice = 2684.42;

      // Save fallback price to cache
      localStorage.setItem(
        "goldPriceCache",
        JSON.stringify({
          price: fallbackPrice,
          timestamp: now,
        })
      );

      setGoldPrice(fallbackPrice);
      setGoldPriceDisplay(fallbackPrice.toFixed(2));
    } finally {
      setIsLoading(false);
    }
  };

  const calculatePlan = () => {
    const newCacheKey = `${formData.balance}-${formData.leverage}-${
      formData.tradingDays
    }-${formData.commission || DEFAULT_COMMISSION_PER_LOT}`;

    // Check if we already have this calculation cached
    if (newCacheKey === cacheKey) {
      return; // Return early if same parameters
    }

    const initialAmount = parseFloat(formData.balance);
    const days = parseInt(formData.tradingDays);
    const leverage = parseFloat(formData.leverage);

    if (!initialAmount || !days || !leverage) return;

    let plan = [];
    let currentAmount = initialAmount;
    let currentDate = new Date();
    let tradingDay = 1;

    // Get current gold price or use fallback
    const currentGoldPrice = goldPrice || 2684.42;

    while (tradingDay <= days) {
      // Skip weekends (6 = Saturday, 0 = Sunday)
      const dayOfWeek = currentDate.getDay();
      if (dayOfWeek === 6 || dayOfWeek === 0) {
        currentDate = addDays(currentDate, 1);
        continue;
      }

      // Calculate max loss (10% risk)
      const maxLoss = currentAmount * 0.1;

      // Calculate margin requirement per lot based on current gold price
      const marginPerLot = (OUNCES_PER_LOT * currentGoldPrice) / leverage;

      // Calculate maximum lots based on available margin
      const maxLotsByMargin = (currentAmount / marginPerLot).toFixed(2);

      // Calculate lots based on risk (maxLoss)
      const lotsByRisk = (maxLoss / (DAILY_PIPS * PIP_VALUE_PER_LOT)).toFixed(
        2
      );

      // Use the smaller of the two lot calculations
      let recommendedLotSize = Math.min(
        parseFloat(maxLotsByMargin),
        parseFloat(lotsByRisk),
        MAX_ALLOWED_LOT_SIZE
      ).toFixed(2);

      // Calculate expected profit and other values (unchanged)
      const expectedProfit = (
        recommendedLotSize *
        PIP_VALUE_PER_LOT *
        DAILY_PIPS
      ).toFixed(2);
      const commissionCost =
        recommendedLotSize *
        (parseFloat(formData.commission) || DEFAULT_COMMISSION_PER_LOT);
      const estimatedBalance = (
        currentAmount +
        parseFloat(expectedProfit) -
        commissionCost
      ).toFixed(2);

      // Ensure max loss doesn't exceed expected profit
      const adjustedMaxLoss = Math.min(
        maxLoss,
        parseFloat(expectedProfit)
      ).toFixed(2);

      plan.push({
        pdfDay: tradingDay,
        date: format(currentDate, "EEE, dd/MM/yyyy"),
        startingBalance: currentAmount.toFixed(2),
        lotSize: recommendedLotSize,
        dailyProfit: expectedProfit, // Shows profit without commission
        maxLoss: adjustedMaxLoss,
        estimatedBalance, // Shows balance after commission is deducted
        riskPercentage: ((adjustedMaxLoss / currentAmount) * 100).toFixed(2),
      });

      currentAmount = parseFloat(estimatedBalance);
      currentDate = addDays(currentDate, 1);
      tradingDay++;
    }

    localStorage.setItem("tradingPlanCache", JSON.stringify(plan));
    localStorage.setItem("tradingPlanCacheKey", newCacheKey);
    localStorage.setItem("tradingPlanFormData", JSON.stringify(formData));

    setCacheKey(newCacheKey);
    setTradingPlan(plan);
    setShowResults(true);
  };

  const resetCalculator = () => {
    localStorage.removeItem("tradingPlanCache");
    localStorage.removeItem("tradingPlanCacheKey");
    localStorage.removeItem("tradingPlanFormData");
    localStorage.removeItem("completedTradingDays");

    setCacheKey("");
    setTradingPlan([]);
    setShowResults(false);
    setCompletedDays([]);
    setFormData({
      balance: "",
      leverage: "",
      tradingDays: "",
      commission: "",
    });
  };

  const generatePDF = () => {
    const container = document.createElement("div");

    // Calculate how many rows can fit on one page (approximately 35 rows per page)
    const rowsPerPage = 35;
    const totalPages = Math.ceil(tradingPlan.length / rowsPerPage);

    let pdfContent = "";

    // Generate content for each page
    for (let page = 0; page < totalPages; page++) {
      const startIdx = page * rowsPerPage;
      const endIdx = Math.min((page + 1) * rowsPerPage, tradingPlan.length);
      const currentPageData = tradingPlan.slice(startIdx, endIdx);

      pdfContent += `
        <div style="padding: 10px; width: 700px; margin: 0 auto; background-color: #ffffff; color: #000000; ${
          page > 0 ? "page-break-before: always;" : ""
        }">
          <div style="text-align: center; margin-bottom: 10px;">
            <h2 style="text-decoration: underline; margin-bottom: 5px; font-size: 14px; font-weight: bold;">$${
              formData.balance
            } to $100k in ${formData.tradingDays} Days Game Plan</h2>
            <h2 style="text-decoration: underline; font-size: 14px; font-weight: bold;">Daily Target 20 Pips/200 Points Daily</h2>
          </div>
          <table style="width: 100%; border-collapse: collapse; margin: 0 auto; font-size: 11px;">
            <thead>
              <tr>
                <th style="border: 1px solid #000; padding: 3px; background-color: #D3D3D3; text-align: center;">DAY</th>
                <th style="border: 1px solid #000; padding: 3px; background-color: #D3D3D3; text-align: center;">DATE</th>
                <th style="border: 1px solid #000; padding: 3px; background-color: #D3D3D3; text-align: center;">BALANCE</th>
                <th style="border: 1px solid #000; padding: 3px; background-color: #D3D3D3; text-align: center;">LOT</th>
                <th style="border: 1px solid #000; padding: 3px; background-color: #D3D3D3; text-align: center;">COMMISSION</th>
                <th style="border: 1px solid #000; padding: 3px; background-color: #D3D3D3; text-align: center;">PROFIT</th>
                <th style="border: 1px solid #000; padding: 3px; background-color: #D3D3D3; text-align: center;">LOSS</th>
                <th style="border: 1px solid #000; padding: 3px; background-color: #D3D3D3; text-align: center;">RESULT</th>
              </tr>
            </thead>
            <tbody>
              ${currentPageData
                .map(
                  (day) => `
                <tr>
                  <td style="border: 1px solid #000; padding: 2px; text-align: center;">${
                    day.pdfDay
                  }</td>
                  <td style="border: 1px solid #000; padding: 2px; text-align: center;">${
                    day.date
                  }</td>
                  <td style="border: 1px solid #000; padding: 2px; text-align: center;">$${formatNumber(
                    day.startingBalance
                  )}</td>
                  <td style="border: 1px solid #000; padding: 2px; text-align: center;">${
                    day.lotSize
                  }</td>
                  <td style="border: 1px solid #000; padding: 2px; text-align: center;">-$${formatNumber(
                    day.lotSize *
                      (parseFloat(formData.commission) ||
                        DEFAULT_COMMISSION_PER_LOT) *
                      2
                  )}</td>
                  <td style="border: 1px solid #000; padding: 2px; text-align: center;">$${formatNumber(
                    day.dailyProfit
                  )}</td>
                  <td style="border: 1px solid #000; padding: 2px; text-align: center;">$${formatNumber(
                    day.maxLoss
                  )}</td>
                  <td style="border: 1px solid #000; padding: 2px; text-align: center;">$${formatNumber(
                    day.estimatedBalance
                  )}</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
          <div style="text-align: center; margin-top: 10px; font-size: 10px; font-style: italic; color: #666;">
            Generate your trading plan at: https://gold.ramiatrouni.com
          </div>
        </div>
      `;
    }

    container.innerHTML = pdfContent;

    const opt = {
      margin: [0.5, 0.5, 0.5, 0.5],
      filename: `TradingPlan-${format(new Date(), "yyyy-MM-dd")}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        logging: false,
      },
      jsPDF: {
        unit: "in",
        format: "a4",
        orientation: "portrait",
      },
      pagebreak: { mode: "avoid-all" },
    };

    html2pdf().set(opt).from(container).save();
  };

  const toggleTheme = () => {
    document.body.classList.add("theme-transitioning");
    setTheme((prevTheme) => (prevTheme === "light" ? "dark" : "light"));

    // Remove the transition class after the animation completes
    setTimeout(() => {
      document.body.classList.remove("theme-transitioning");
    }, 300);
  };

  const goldPriceElement = (
    <span className="text-success d-flex align-items-center">
      <span className="d-none d-md-inline">Gold Price: </span>
      {isLoading ? (
        <span
          className="spinner-border spinner-border-sm"
          role="status"
          aria-hidden="true"
        />
      ) : (
        <>
          <span>${goldPriceDisplay}</span>
          <i
            className="fas fa-info-circle ms-1"
            style={{ cursor: "help" }}
            data-bs-toggle="tooltip"
            data-bs-placement="right"
            title="The gold price updates when you refresh the page. When the price changes, it affects your trading plan calculations, including lot sizes and profit targets."
          ></i>
        </>
      )}
    </span>
  );

  // Add new state for form visibility
  const [isFormCollapsed, setIsFormCollapsed] = useState(() => {
    return localStorage.getItem("tradingPlanCache") !== null;
  });

  const renderForm = () => (
    <div className="form-container">
      <div
        className={`form-header ${showResults ? "cursor-pointer" : ""}`}
        onClick={() => showResults && setIsFormCollapsed(!isFormCollapsed)}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Trading Plan Parameters</h5>
          {showResults && (
            <button
              className="btn btn-link p-0 text-decoration-none"
              aria-label={isFormCollapsed ? "Expand form" : "Collapse form"}
            >
              <i
                className={`fas ${
                  isFormCollapsed ? "fa-chevron-down" : "fa-chevron-up"
                } 
                transition-icon ${
                  theme === "dark" ? "text-light" : "text-dark"
                }`}
              ></i>
            </button>
          )}
        </div>
      </div>

      <div className={`collapse ${!isFormCollapsed ? "show" : ""}`}>
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="mb-3">
            <label className="form-label">
              Current Balance ($) <span className="text-danger">*</span>
            </label>
            <input
              type="number"
              name="balance"
              className="form-control"
              value={formData.balance}
              onChange={handleInputChange}
              placeholder="Enter current balance (max $100,000)"
              min="0.01"
              max="1000000"
              step="0.01"
              required
            />
          </div>

          <div className="mb-3">
            <label className="form-label">
              Leverage <span className="text-danger">*</span>
            </label>
            <select
              name="leverage"
              className="form-control"
              value={formData.leverage}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Leverage</option>
              {LEVERAGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-3">
            <label className="form-label">
              Number of Trading Days <span className="text-danger">*</span>
            </label>
            <input
              type="number"
              name="tradingDays"
              className="form-control"
              value={formData.tradingDays}
              onChange={handleInputChange}
              placeholder="Enter number of trading days (max 365)"
              min="1"
              max="365"
              step="1"
              required
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Commission per Lot ($)</label>
            <input
              type="number"
              name="commission"
              className="form-control"
              value={formData.commission}
              onChange={handleInputChange}
              placeholder={`Enter commission per lot (default: $${DEFAULT_COMMISSION_PER_LOT} / lot)`}
              min="0"
              step="0.01"
            />
            <div className="form-text text-danger">
              Leave empty to use the default commission of $
              {DEFAULT_COMMISSION_PER_LOT} per lot - (Tickmill)
            </div>
          </div>

          <div className="d-flex gap-2">
            <button
              type="button"
              className={`btn ${
                balanceError ? "btn-danger" : "btn-primary"
              } flex-grow-1`}
              onClick={calculatePlan}
              disabled={
                !formData.balance ||
                !formData.leverage ||
                !formData.tradingDays ||
                balanceError
              }
            >
              {balanceError ? "Minimum 100$ !!!!" : "Generate Trading Plan"}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={resetCalculator}
            >
              Reset
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  // Add new state for completed days
  const [completedDays, setCompletedDays] = useState(() => {
    const saved = localStorage.getItem("completedTradingDays");
    return saved ? JSON.parse(saved) : [];
  });

  // Add new states for confetti
  const [showConfetti, setShowConfetti] = useState(false);
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: document.documentElement.clientHeight,
  });

  // Update useEffect for window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: document.documentElement.clientHeight,
      });
    };

    // Add resize event listener
    window.addEventListener("resize", handleResize);

    // Initial setup
    handleResize();

    // Cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update handleDayCompletion function
  const handleDayCompletion = (dayNumber) => {
    if (completedDays.includes(dayNumber)) {
      // When unchecking, only allow if it's the last checked day
      if (dayNumber === Math.max(...completedDays)) {
        const newCompletedDays = completedDays.filter(
          (day) => day !== dayNumber
        );
        setCompletedDays(newCompletedDays);
        localStorage.setItem(
          "completedTradingDays",
          JSON.stringify(newCompletedDays)
        );
      }
    } else {
      // When checking, only allow if it's the next day in sequence
      const nextExpectedDay =
        completedDays.length === 0 ? 1 : Math.max(...completedDays) + 1;
      if (dayNumber === nextExpectedDay) {
        const newCompletedDays = [...completedDays, dayNumber];
        setCompletedDays(newCompletedDays);
        localStorage.setItem(
          "completedTradingDays",
          JSON.stringify(newCompletedDays)
        );

        // Trigger confetti
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000); // Hide confetti after 3 seconds
      }
    }
  };

  // Add this useEffect to initialize tooltips after the component mounts
  useEffect(() => {
    // Initialize all tooltips
    const tooltipTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );

    // Import bootstrap dynamically to avoid undefined error
    import("bootstrap").then((bootstrap) => {
      tooltipTriggerList.forEach((tooltipTriggerEl) => {
        new bootstrap.Tooltip(tooltipTriggerEl);
      });

      // Cleanup tooltips on component unmount
      return () => {
        tooltipTriggerList.forEach((tooltipTriggerEl) => {
          const tooltip = bootstrap.Tooltip.getInstance(tooltipTriggerEl);
          if (tooltip) {
            tooltip.dispose();
          }
        });
      };
    });
  }, []);

  // Add new state for modal visibility after other useState declarations
  const [showOverviewModal, setShowOverviewModal] = useState(false);

  // Add modal component before the return statement
  const renderOverviewModal = () => (
    <div
      className={`modal fade ${showOverviewModal ? "show" : ""}`}
      style={{ display: showOverviewModal ? "block" : "none" }}
      tabIndex="-1"
      aria-modal="true"
      role="dialog"
    >
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title fw-bold">Trading Plan Overview</h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => setShowOverviewModal(false)}
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            <div className="row g-4">
              {/* First Row */}
              <div className="col-md-6 col-lg-3">
                <div className="summary-card h-100 p-3 bg-light rounded-3 shadow-sm">
                  <h6 className="text-muted mb-3">Initial Balance</h6>
                  <div className="d-flex flex-column">
                    <span className="fs-4 fw-bold">
                      ${formatNumber(formData.balance)}
                    </span>
                    <span className="text-muted small">Starting Capital</span>
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-3">
                <div className="summary-card h-100 p-3 bg-light rounded-3 shadow-sm">
                  <h6 className="text-muted mb-3">Final Balance</h6>
                  <div className="d-flex flex-column">
                    <span className="fs-4 fw-bold text-success">
                      $
                      {formatNumber(
                        tradingPlan[tradingPlan.length - 1]?.estimatedBalance
                      )}
                    </span>
                    <span className="text-muted small">
                      Final Account Value
                    </span>
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-3">
                <div className="summary-card h-100 p-3 bg-light rounded-3 shadow-sm">
                  <h6 className="text-muted mb-3">ROI</h6>
                  <div className="d-flex flex-column">
                    <span className="fs-4 fw-bold text-success">
                      {(
                        (tradingPlan[tradingPlan.length - 1]?.estimatedBalance /
                          formData.balance -
                          1) *
                        100
                      ).toFixed(2)}
                      %
                    </span>
                    <span className="text-muted small">
                      Total Return on Investment
                    </span>
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-3">
                <div className="summary-card h-100 p-3 bg-light rounded-3 shadow-sm">
                  <h6 className="text-muted mb-3">Total Commission</h6>
                  <div className="d-flex flex-column">
                    <span className="fs-4 fw-bold text-danger">
                      -$
                      {formatNumber(
                        tradingPlan.reduce(
                          (total, day) =>
                            total +
                            day.lotSize *
                              (parseFloat(formData.commission) ||
                                DEFAULT_COMMISSION_PER_LOT) *
                              2,
                          0
                        )
                      )}
                    </span>
                    <span className="text-muted small">Total Trading Cost</span>
                  </div>
                </div>
              </div>

              {/* Second Row */}
              <div className="col-md-6 col-lg-3">
                <div className="summary-card h-100 p-3 bg-light rounded-3 shadow-sm">
                  <h6 className="text-muted mb-3">Initial Lot Size</h6>
                  <div className="d-flex flex-column">
                    <span className="fs-4 fw-bold">
                      {tradingPlan[0]?.lotSize}
                    </span>
                    <span className="text-muted small">Starting Lots</span>
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-3">
                <div className="summary-card h-100 p-3 bg-light rounded-3 shadow-sm">
                  <h6 className="text-muted mb-3">Final Lot Size</h6>
                  <div className="d-flex flex-column">
                    <span className="fs-4 fw-bold">
                      {tradingPlan[tradingPlan.length - 1]?.lotSize}
                    </span>
                    <span className="text-muted small">Ending Lots</span>
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-3">
                <div className="summary-card h-100 p-3 bg-light rounded-3 shadow-sm">
                  <h6 className="text-muted mb-3">Trading Period</h6>
                  <div className="d-flex flex-column">
                    <span className="fs-4 fw-bold">{tradingPlan.length}</span>
                    <span className="text-muted small">Trading Days</span>
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-3">
                <div className="summary-card h-100 p-3 bg-light rounded-3 shadow-sm">
                  <h6 className="text-muted mb-3">Daily Target</h6>
                  <div className="d-flex flex-column">
                    <span className="fs-4 fw-bold">{DAILY_PIPS}</span>
                    <span className="text-muted small">Pips per Day</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 text-center">
              <div className="badge bg-info p-2 me-2">
                <i className="bi bi-info-circle me-1"></i>
                Leverage: 1:{formData.leverage}
              </div>
              <div className="badge bg-warning text-dark p-2">
                <i className="bi bi-shield-check me-1"></i>
                Risk per trade: 10%
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowOverviewModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {showConfetti && (
        <Confetti
          width={windowDimensions.width}
          height={windowDimensions.height}
          recycle={false}
          numberOfPieces={200}
          gravity={0.3}
          style={{
            position: "fixed", // Fix position to viewport
            top: 0,
            left: 0,
            zIndex: 9999, // Ensure it's above other elements
            width: "100%",
            height: "100%",
            pointerEvents: "none", // Prevent confetti from blocking interactions
          }}
        />
      )}
      <div className="container mt-4">
        <div className="card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h4 className="mb-0">
              <span className="d-none d-md-inline">
                Gold Trading Calculator (200 Pips Daily)
              </span>
              <span className="d-inline d-md-none">Gold (200 Pips)</span>
            </h4>
            <div className="d-flex align-items-center gap-3">
              {goldPriceElement}
              <button
                className="btn btn-outline-secondary d-flex align-items-center gap-2"
                onClick={toggleTheme}
                aria-label="Toggle theme"
              >
                {theme === "light" ? (
                  <>
                    <span className="d-none d-md-inline">Dark Mode</span>
                    <span className="theme-icon" key="dark">
                      🌙
                    </span>
                  </>
                ) : (
                  <>
                    <span className="d-none d-md-inline">Light Mode</span>
                    <span className="theme-icon" key="light">
                      ☀️
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>
          <div className="card-body">
            <div className="alert alert-info">
              {/* Mobile View (Condensed) */}
              <div className="d-md-none">
                <h6 className="fw-bold mb-3">Quick Trading Guide:</h6>
                <ul className="mb-0 ps-3">
                  <li>0.01 lot = $0.01 per pip (200 pips = $2)</li>
                  <li>Risk limit: 10% of balance per day</li>
                  <li>Best time: London/NY sessions</li>
                  <li>Use 3 mins / The V / EPE</li>
                </ul>
              </div>

              {/* Desktop View (Full) */}
              <div className="d-none d-md-block">
                <div className="row gy-4">
                  <div className="col-12 col-md-4 mb-3 mb-md-0">
                    <h6 className="fw-bold mb-3">Basic Info:</h6>
                    <ul className="mb-0 ps-3">
                      <li>0.01 lot = $0.01 per pip</li>
                      <li>Example: 200 pips with 0.08 lot = $16 profit</li>
                      <li>Daily target: 200 pips</li>
                    </ul>
                  </div>
                  <div className="col-12 col-md-4 mb-3 mb-md-0">
                    <h6 className="fw-bold mb-3">Risk Management:</h6>
                    <ul className="mb-0 ps-3">
                      <li>Risk per trade: 10% of balance</li>
                      <li>Stop trading if you hit daily loss limit</li>
                      <li>وقف التداول عند الوصول للخسارة اليومية</li>
                    </ul>
                  </div>
                  <div className="col-12 col-md-4">
                    <h6 className="fw-bold mb-3">Trading Hints:</h6>
                    <ul className="mb-0 ps-3">
                      <li>Trade during London/NY sessions</li>
                      <li>Use 3 mins / The V / EPE</li>
                      <li>Avoid news releases</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {renderForm()}
          </div>
        </div>

        {showResults && (
          <div className="card results-fade-in">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h4 className="mb-0">
                Trading Plan for {formData.tradingDays} Days
              </h4>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-primary"
                  onClick={() => setShowOverviewModal(true)}
                >
                  <i className="fas fa-chart-line me-2"></i>Overview
                </button>
                <button className="btn btn-success" onClick={generatePDF}>
                  <i className="fas fa-download me-2"></i>Download PDF
                </button>
              </div>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table
                  id="trading-table"
                  className="table table-striped table-bordered"
                  style={{ position: "relative" }}
                >
                  <thead
                    style={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: "white",
                      zIndex: 1,
                    }}
                  >
                    <tr>
                      <th style={{ width: "50px" }}>Done</th>
                      <th className="d-none d-md-table-cell">Day</th>
                      <th className="d-none d-md-table-cell">Date</th>
                      <th>
                        <span className="d-none d-md-inline">
                          Starting Balance ($)
                        </span>
                        <span className="d-md-none">Bal ($)</span>
                      </th>
                      <th>Lot</th>
                      <th className="d-none d-md-table-cell">Commission ($)</th>
                      <th>
                        <span className="d-none d-md-inline">
                          Expected Profit ($)
                        </span>
                        <span className="d-md-none">Profit ($)</span>
                      </th>
                      <th className="table-danger d-none d-md-table-cell">
                        Max Loss ($)
                      </th>
                      <th className="d-none d-md-table-cell">
                        Estimated Balance ($)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tradingPlan.map((day, index) => (
                      <tr
                        key={day.pdfDay}
                        className="table-row-animate"
                        style={{
                          animationDelay: `${index * 50}ms`,
                          opacity: 0,
                          animationFillMode: "forwards",
                          textDecoration: completedDays.includes(day.pdfDay)
                            ? "line-through"
                            : "none",
                        }}
                      >
                        <td className="text-center">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={completedDays.includes(day.pdfDay)}
                            onChange={() => handleDayCompletion(day.pdfDay)}
                            style={{
                              cursor:
                                completedDays.includes(day.pdfDay) ||
                                day.pdfDay ===
                                  (completedDays.length === 0
                                    ? 1
                                    : Math.max(...completedDays) + 1)
                                  ? "pointer"
                                  : "not-allowed",
                            }}
                            disabled={
                              !completedDays.includes(day.pdfDay) &&
                              day.pdfDay !==
                                (completedDays.length === 0
                                  ? 1
                                  : Math.max(...completedDays) + 1)
                            }
                          />
                        </td>
                        <td className="d-none d-md-table-cell">
                          Day {day.pdfDay}
                        </td>
                        <td className="d-none d-md-table-cell">{day.date}</td>
                        <td>${formatNumber(day.startingBalance)}</td>
                        <td>{day.lotSize}</td>
                        <td className="text-danger d-none d-md-table-cell">
                          -$
                          {formatNumber(
                            day.lotSize *
                              (parseFloat(formData.commission) ||
                                DEFAULT_COMMISSION_PER_LOT) *
                              2
                          )}
                        </td>
                        <td className="text-success">
                          ${formatNumber(day.dailyProfit)}
                        </td>
                        <td className="text-danger d-none d-md-table-cell">
                          -${formatNumber(day.maxLoss)}
                        </td>
                        <td className="d-none d-md-table-cell">
                          ${formatNumber(day.estimatedBalance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
      {showOverviewModal && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={() => setShowOverviewModal(false)}
          ></div>
          {renderOverviewModal()}
        </>
      )}
      <footer className="text-center py-3 mt-4">
        <p className="mb-0">
          © {new Date().getFullYear()}{" "}
          <a
            href="https://ramiatrouni.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-decoration-none"
          >
            Rami Atrouni
          </a>
          . All rights reserved.
        </p>
      </footer>
    </div>
  );
}

export default App;
